<template>
  <v-card outlined :style="`border-color: ${color}`">
    <div :style="`background-color: ${color}; height: 18px`"></div>
    <div class="pt-0">
      <AlertsAnimated :alerts="localAlerts" />
      <v-col>
        <v-row justify="space-between" align="center">
          <v-card-title class="subtitle-1 black--text">
            {{ "Manage Tasks" }}
          </v-card-title>
        </v-row>
      </v-col>

      <v-col>
        <v-row dense align="center">
          <v-col class="col-lg-3">
            <div class="d-flex align-center">
              <DateTimeFilter
                v-model="dateRange"
                :time-zone="timeZone"
                :ranges="['1w', '1m']"
                :max="maxDate"
                max-range="1m"
                class="black-border"
              />
              <TaskFilter
                class="ml-2"
                :auto-filter="autoFilter"
                @apply="applyFilter"
              />
            </div>
          </v-col>
          <v-col class="pl-6 pr-8 col-lg-8 col-md-6 col-sm-6">
            <SearchField v-model="tableState.search" />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <TaskCalendar
          :date-range="calendarDateRange"
          :tasks="combinedTasksForCalendar"
          :time-zone="timeZone"
          :loading="tasksLoading"
          @task-updated="handleTaskUpdate"
          @open-task-dialog="openTaskDialog"
        />
      </v-col>

      <v-skeleton-loader
        :loading="tasksLoading"
        type="table-thead, table-row-divider@10"
      >
        <v-data-table
          :headers="headers"
          :items="allTableData"
          :custom-filter="customFilter"
          :custom-sort="customSort"
          :search="tableState.search"
          :sort-by.sync="tableState.sortBy"
          :sort-desc.sync="tableState.sortDesc"
          :page.sync="tableState.page"
          :items-per-page.sync="tableState.itemsPerPage"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
          :loading="tasksLoading"
        >
          <template #item="{ item }">
            <tr :class="{ 'overdue-row': item.isOverdue }">
              <td>
                <ButtonView title="View" @click="openTaskDialog(item)" />
              </td>
              <td style="min-width: 50px">
                {{ item.taskId }}
              </td>
              <td style="min-width: 300px">
                <div class="mt-2">
                  <v-icon color="none" title="Organization" v-bind="$attrs">
                    mdi-sitemap
                  </v-icon>
                  {{ item.task_location.organization }}
                </div>
                <div class="mt-2">
                  <v-icon color="none" title="Facility" v-bind="$attrs">
                    mdi-domain
                  </v-icon>
                  {{ item.task_location.facility }}
                </div>
                <div class="mt-2" v-if="item.task_location.smart_unit">
                  <v-icon color="none" title="Unit/Zone" v-bind="$attrs">
                    mdi-garage-variant
                  </v-icon>
                  {{ item.task_location.smart_unit }}
                </div>
              </td>
              <td>{{ item.name }}</td>
              <td>{{ item.type }}</td>
              <td>
                {{
                  item.assignee == "SD Automation"
                    ? "Regional Manager"
                    : item.assignee
                }}
              </td>
              <td>
                <div>
                  {{ item.status }}
                  <span v-if="item.overdue" class="overdue-text">Overdue!</span>
                </div>
              </td>
              <td>
                <SeverityChip :severity="item.priority" />
              </td>
              <td>
                {{ item.due_date }}
              </td>
            </tr>
          </template>
          <template #footer.prepend>
            <ButtonExportCSV
              class="order-last"
              :table-data="allTableData"
              :headers="csvHeaders"
              filename="Tasks Report"
            />
            <ButtonPrint
              class="order-last"
              :before-print="showAllItems"
              :after-print="restoreItemsPerPage"
            />
          </template>
        </v-data-table>
      </v-skeleton-loader>

      <DialogViewTask
        v-if="isOpenViewTask && !isOpenPreInstallModal"
        v-model="isOpenViewTask"
        :task-id="selectedTaskId"
        @task-updated="handleTaskUpdate"
      />
      <DialogPreInstallModal
        v-if="isOpenViewTask && isOpenPreInstallModal"
        v-model="isOpenViewTask"
        :task="selectedTask"
        @task-updated="handleTaskUpdate"
      />
    </div>
  </v-card>
</template>

<script>
import { ref, computed } from "vue";
import SearchField from "@atoms/SearchField.vue";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";
import {
  TASK_TYPES,
  DASHBOARD_OVERDUE_TASKS,
  DASHBOARD_FUTURE_TASKS,
} from "./graphql";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import useDataTable from "@components/common/useDataTable";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import ButtonView from "@atoms/ButtonView.vue";
import { titleCase } from "@tod-ui/helpers/strings";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import DialogViewTask from "./DialogViewTask.vue";
import DialogPreInstallModal from "./DialogPreInstallModal.vue";
import { formatName } from "./helper";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import { DateTime } from "luxon";
import TaskFilter from "./TaskFilter.vue";
import TaskCalendar from "./TaskCalendar.vue";
import useFacility from "@components/authentication/useFacility";
import useAlerts from "@tod-ui/composables/useAlerts";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";

export default {
  name: "PageManageTaskDashboard",
  components: {
    TaskFilter,
    SearchField,
    ButtonExportCSV,
    ButtonPrint,
    ButtonView,
    SeverityChip,
    DialogViewTask,
    DialogPreInstallModal,
    DateTimeFilter,
    TaskCalendar,
    AlertsAnimated,
  },
  props: {
    color: {
      type: String,
      default: "#FF9E00",
    },
    overdueCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const { defaultOrganization } = useUser();
    const { currentFacilityId } = useFacility();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
    });
    const { addAlert, addLocalAlert, clearAllAlerts, localAlerts } =
      useAlerts();

    const autoFilter = ref({
      status: "",
      overdue: true,
      resolutions: ["todo", "in_progress", "in_review", "pending"],
      dateRange: { after: null, before: null },
    });
    const isOpenViewTask = ref(false);
    const selectedTaskId = ref({});
    const selectedTask = ref(null);
    const isOpenPreInstallModal = ref(false);
    const dateRange = ref({ after: undefined, before: undefined, range: "1w" });
    const maxDate = DateTime.now().plus({ years: 1 }).toISO();
    const timeZone = ref(defaultOrganization.timeZone);
    const taskTypes = ref([]);
    const overdueTasksData = ref([]);
    const futureTasksData = ref([]);
    const overdueTotal = ref(0);
    const futureTotal = ref(0);
    const filter = ref({});

    return {
      defaultOrganization,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      isOpenViewTask,
      selectedTaskId,
      selectedTask,
      isOpenPreInstallModal,
      dateRange,
      maxDate,
      timeZone,
      autoFilter,
      currentFacilityId,
      addAlert,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
      taskTypes,
      overdueTasksData,
      futureTasksData,
      overdueTotal,
      futureTotal,
      filter,
    };
  },

  computed: {
    tasksLoading() {
      return (
        this.$apollo.queries.dashboardOverdueTasks.loading ||
        this.$apollo.queries.dashboardFutureTasks.loading
      );
    },
    defaultTaskTypes() {
      return this.taskTypes
        .filter((type) => type.name !== "incident_resolution")
        .map((type) => type.id);
    },
    queryTaskTypes() {
      if (this.filter.taskTypes && this.filter.taskTypes.length > 0) {
        return this.filter.taskTypes;
      } else {
        return this.defaultTaskTypes;
      }
    },
    calendarDateRange() {
      if (this.dateRange.range) {
        const now = DateTime.now().setZone(this.timeZone);

        if (this.dateRange.range === "1w") {
          const after = now.startOf("day").toUTC();
          const before = after.plus({ days: 6 }).endOf("day");

          return {
            ...this.dateRange,
            after: after.toISO(),
            before: before.toISO(),
          };
        } else if (this.dateRange.range === "1m") {
          const after = now.startOf("day").toUTC();
          const before = after
            .plus({ months: 1 })
            .minus({ days: 1 })
            .endOf("day");

          return {
            ...this.dateRange,
            after: after.toISO(),
            before: before.toISO(),
          };
        }
      }
      return this.dateRange;
    },
    combinedTasksForCalendar() {
      const overdueTasks = this.overdueTasksData || [];
      //const futureTasks = this.futureTasksData || [];// need to be replaced with below code

      const futureTasks =
        this.futureTasksData.map((task) => ({
          ...task,
          overdue: false,
        })) || [];

      const today = DateTime.now().setZone(this.timeZone).startOf("day");
      const processedOverdueTasks = overdueTasks.map((task) => ({
        ...task,
        originalDueAt: task.dueAt,
        dueAt: today.toISO(),
        isStackedOverdue: true,
        overdue: true,
      }));

      const allTasks = [...processedOverdueTasks, ...futureTasks];

      const uniqueTasks = allTasks.filter(
        (task, index, self) => index === self.findIndex((t) => t.id === task.id)
      );

      return uniqueTasks;
    },
    futureTasksDateRange() {
      const now = DateTime.utc();
      const range = this.dateRange.range || "1w";

      let before;
      if (range === "1w") {
        before = now.plus({ days: 6 }).endOf("day");
      } else if (range === "1m") {
        before = now.plus({ months: 1 }).minus({ days: 1 }).endOf("day");
      }

      return {
        after: now.startOf("day").toISO(),
        before: before.toISO(),
      };
    },
    combinedTotalItems() {
      return this.overdueTotal + this.futureTotal;
    },
    allTableData() {
      const overdueData = this.overdueTasksData.map((task) => ({
        ...this.formatTaskForTable(task),
        isOverdue: true,
        originalDueDate: formatDateSlashUTC(task.dueAt),
        sortOrder: 1,
        originalDueAt: task.dueAt,
      }));

      const futureData = this.futureTasksData.map((task) => ({
        ...this.formatTaskForTable(task, true), // need to be replaced with below code
        isOverdue: false,
        sortOrder: 2,
        originalDueAt: task.dueAt,
        overdue: false, // need to be replaced with below code
      }));

      const sortedOverdue = overdueData.sort(
        (a, b) => new Date(a.originalDueAt) - new Date(b.originalDueAt)
      );

      const sortedFuture = futureData.sort(
        (a, b) => new Date(a.originalDueAt) - new Date(b.originalDueAt)
      );

      return [...sortedOverdue, ...sortedFuture];
    },

    headers() {
      return [
        { text: "View Task", value: "" },
        { text: "Task ID", value: "taskId" },
        { text: "Task Location", value: "task_location", width: "150" },
        { text: "Task Name", value: "name" },
        { text: "Task Type", value: "type" },
        { text: "Assignee", value: "assignee", width: "150" },
        { text: "Task Status", value: "status" },
        { text: "Task Priority", value: "priority" },
        { text: "Task Due Date", value: "due_date", width: "150" },
      ];
    },
    csvHeaders() {
      return [
        { text: "Task ID", value: "taskId" },
        { text: "Organization", value: "organization" },
        { text: "Facility", value: "facility" },
        { text: "Smart Unit / Zone", value: "smart_unit" },
        { text: "Task Name", value: "name" },
        { text: "Task Type", value: "type" },
        { text: "Assignee", value: "assignee" },
        { text: "Task Status", value: "status" },
        { text: "Task Priority", value: "priority" },
        { text: "Task Due Date", value: "due_date" },
      ];
    },
  },

  apollo: {
    taskTypes: {
      query: TASK_TYPES,
      variables() {
        return {
          sort: "name",
          status: "active",
        };
      },
      result({ data }) {
        this.taskTypes = data?.taskTypes || [];
      },
      error(error) {
        console.error("Error fetching task types:", error);
      },
    },
    dashboardOverdueTasks: {
      query: DASHBOARD_OVERDUE_TASKS,
      skip() {
        return (
          this.queryTaskTypes?.length === 0 || this.filter.overdue === false
        );
      },
      variables() {
        const limit = Math.max(this.overdueCount || 100, 100);
        const yesterday = DateTime.utc().minus({ days: 1 }).startOf("day");

        return {
          parentId: this.currentFacilityId,
          taskTypes: this.queryTaskTypes,
          dateTimeFilter: {
            before: yesterday.toISO(), // This will give you "2026-02-26T00:00:00.000Z" for today being 27th
          },
          limit: limit,
          ...this.filter,
        };
      },
      update({ tasksPaginated }) {
        this.overdueTotal = tasksPaginated.page.totalCount;
        this.overdueTasksData = tasksPaginated.data || [];
        return tasksPaginated.data || [];
      },
      error(error) {
        console.error("Error fetching dashboard overdue tasks:", error);
      },
    },
    dashboardFutureTasks: {
      query: DASHBOARD_FUTURE_TASKS,
      skip() {
        return this.queryTaskTypes?.length === 0;
      },
      variables() {
        return {
          parentId: this.currentFacilityId,
          dateTimeFilter: this.futureTasksDateRange,
          taskTypes: this.queryTaskTypes,
          limit: 100,
          ...this.filter,
        };
      },
      update({ tasksPaginated }) {
        this.futureTotal = tasksPaginated.page.totalCount;
        this.futureTasksData = tasksPaginated.data || [];
        return tasksPaginated.data || [];
      },
      error(error) {
        console.error("Error fetching dashboard future tasks:", error);
      },
    },
  },

  watch: {
    "dateRange.range"() {
      this.$apollo.queries.dashboardFutureTasks.refetch();
    },
    "tableState.itemsPerPage"() {
      this.tableState.page = 1;
    },
  },

  methods: {
    applyFilter(filter) {
      const _filter = { ...filter };

      if (_filter.overdue !== undefined) {
        _filter.isOverdue = _filter.overdue;
      }

      if (_filter.taskStatuses?.length) {
        _filter.resolutions = _filter.taskStatuses;
        delete _filter.taskStatuses;
      }

      if (_filter.locations?.length) {
        _filter.organizationSubtypes = _filter.locations;
        delete _filter.locations;
      }

      for (let k in _filter) {
        if (_filter[k] === null) {
          delete _filter[k];
        } else if (
          typeof _filter[k] === "boolean" &&
          !_filter[k] &&
          k !== "overdue"
        ) {
          delete _filter[k];
        } else if (Array.isArray(_filter[k]) && !_filter[k].length) {
          delete _filter[k];
        }
      }

      if (_filter.afterHours) {
        _filter["duringAccessHours"] =
          _filter.afterHours === "after-hours" ? false : undefined;
      }
      if (_filter.overdue === false) {
        this.overdueTotal = 0;
        this.overdueTasksData = [];
      }

      this.filter = _filter;
      this.tableState.page = 1;
    },
    openTaskDialog(item) {
      const taskTypeName = item.originalTask?.taskType?.name;
      this.isOpenPreInstallModal = taskTypeName === "pre_install_devices";
      this.selectedTaskId = item.id;
      this.selectedTask = item.originalTask || null;
      this.isOpenViewTask = true;
    },
    customFilter(value, search, item) {
      //client side-search added, in future will be changed to server-side search
      if (!search) return true;

      const _search = search.toLowerCase().trim();
      if (!_search) return true;

      const searchableFields = [
        item.taskId || "",
        item.task_location?.organization || "",
        item.task_location?.facility || "",
        item.task_location?.smart_unit || "",
        item.name || "",
        item.type || "",
        item.assignee || "",
        item.status || "",
        item.priority || "",
        item.due_date || "",
      ];

      const searchText = searchableFields
        .filter((field) => field)
        .join(" ")
        .toLowerCase();

      return searchText.includes(_search);
    },
    customSort(items, sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) {
        return items;
      }

      const sortKey = sortBy[0];
      const isDesc = sortDesc[0];

      return items.slice().sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];

        if (sortKey === "task_location") {
          aVal = `${a.task_location?.organization || ""} ${
            a.task_location?.facility || ""
          } ${a.task_location?.smart_unit || ""}`.trim();
          bVal = `${b.task_location?.organization || ""} ${
            b.task_location?.facility || ""
          } ${b.task_location?.smart_unit || ""}`.trim();
        }

        if (sortKey === "due_date") {
          aVal = new Date(a.originalDueAt || a.due_date);
          bVal = new Date(b.originalDueAt || b.due_date);
        }

        if (sortKey === "taskId") {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        }

        if (typeof aVal !== "number" && !(aVal instanceof Date)) {
          aVal = String(aVal || "").toLowerCase();
          bVal = String(bVal || "").toLowerCase();
        }

        let result;
        if (aVal < bVal) {
          result = -1;
        } else if (aVal > bVal) {
          result = 1;
        } else {
          result = 0;
        }

        return isDesc ? -result : result;
      });
    },
    refetchTasks() {
      this.$apollo.queries.dashboardOverdueTasks.refetch();
      this.$apollo.queries.dashboardFutureTasks.refetch();
    },
    handleTaskUpdate(updateData) {
      this.refetchTasks();
      this.$emit("task-updated", updateData);
    },
    formatTaskForTable(task, isFutureTask = false) {
      return {
        id: task.id,
        taskId: task.taskId,
        task_location: {
          organization: task.organization.organization[0]?.name || "",
          facility: task.organization.facility[0]?.name || "",
          smart_unit:
            task.organization.type === "facility" ? "" : task.organization.name,
        },
        name: task.title,
        type: titleCase(task.taskType.name),
        assignee: formatName(task.assignee),
        status:
          task.resolution === RESOLUTION_STATUS.TODO
            ? "To Do"
            : titleCase(task.resolution),
        priority: titleCase(task.priority),
        due_date: formatDateSlashUTC(task.dueAt),
        overdue: isFutureTask ? false : task.overdue,
        originalTask: task,
      };
    },
  },
};
</script>

<style scoped>
.overdue-text {
  color: red;
  font-weight: bold;
}
</style>
